import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { Account, AccountType, OverdraftType } from '../types';
import { updateAccount } from '../services/financeService';
import { useFirebase } from './FirebaseProvider';
import { INSTITUTIONS } from '../constants/institutions';
import { motion, AnimatePresence } from 'motion/react';

interface EditAccountModalProps {
  account: Account;
  onClose: () => void;
}

export const EditAccountModal: React.FC<EditAccountModalProps> = ({ account, onClose }) => {
  const { user, effectiveUserId } = useFirebase();
  const [name, setName] = useState(account.name);
  const [type, setType] = useState<AccountType>(account.type);
  const [institutionId, setInstitutionId] = useState<string>(account.institutionId || 'efectivo');

  const [creditLimit, setCreditLimit] = useState<number>(account.creditLimit || 0);
  const [overdraftType, setOverdraftType] = useState<OverdraftType>(account.overdraftType || 'NONE');
  const [overdraftValue, setOverdraftValue] = useState<number>(account.overdraftValue || 0);
  const [isActive, setIsActive] = useState<boolean>(account.isActive !== false);

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveUserId || !name.trim()) return;

    setIsSaving(true);
    try {
      await updateAccount(effectiveUserId, account.id, {
        name: name.trim(),
        type,
        institutionId: institutionId || null,
        isActive,
        creditLimit: (type === 'TARJETA DE CREDITO' || type === 'BANCOS') ? creditLimit : 0,
        overdraftType: (type === 'TARJETA DE CREDITO' || type === 'BANCOS') ? overdraftType : 'NONE',
        overdraftValue: (type === 'TARJETA DE CREDITO' || type === 'BANCOS') ? overdraftValue : 0,
      });
      onClose();
    } catch (error) {
      console.error('Error updating account:', error);
      alert('Hubo un error al actualizar la cuenta.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-zinc-100">Editar Medio de Pago</h2>
          <button 
            onClick={onClose}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Nombre de la Cuenta
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                placeholder="Ej: Billetera, Banco Itaú..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Tipo de Cuenta
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AccountType)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none"
              >
                <option value="EFECTIVO">Efectivo</option>
                <option value="BANCOS">Bancos</option>
                <option value="TARJETA DE CREDITO">Tarjeta de Crédito</option>
                <option value="OTROS">Otros</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                Entidad Financiera
              </label>
              <select
                value={institutionId}
                onChange={(e) => setInstitutionId(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none"
              >
                {INSTITUTIONS.map(inst => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>

            {(type === 'TARJETA DE CREDITO' || type === 'BANCOS') && (
              <div className="p-4 bg-zinc-800/30 border border-zinc-800 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-zinc-300">Configuración de Límites</h3>
                
                {type === 'TARJETA DE CREDITO' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      Línea de Crédito Total
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">Gs.</span>
                      <input
                        type="number"
                        value={creditLimit}
                        onChange={(e) => setCreditLimit(Number(e.target.value))}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                )}
                
                {type === 'BANCOS' && (
                  <div>
                    <label className="block text-sm font-medium text-zinc-400 mb-2">
                      Línea de Sobregiro Principal
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">Gs.</span>
                      <input
                        type="number"
                        value={creditLimit}
                        onChange={(e) => setCreditLimit(Number(e.target.value))}
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-2">
                    Línea Excedente / Segundo Sobregiro
                  </label>
                  <select
                    value={overdraftType}
                    onChange={(e) => setOverdraftType(e.target.value as OverdraftType)}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none mb-3"
                  >
                    <option value="NONE">Sin sobregiro adicional</option>
                    <option value="FIXED">Importe Fijo</option>
                    {type === 'TARJETA DE CREDITO' && <option value="PERCENTAGE">Porcentaje de Línea</option>}
                  </select>

                  <AnimatePresence>
                    {overdraftType !== 'NONE' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-bold">
                            {overdraftType === 'PERCENTAGE' ? '%' : 'Gs.'}
                          </span>
                          <input
                            type="number"
                            value={overdraftValue}
                            onChange={(e) => setOverdraftValue(Number(e.target.value))}
                            className="w-full bg-zinc-950 border border-zinc-800 rounded-xl p-3 pl-12 text-white focus:outline-none focus:border-emerald-500 transition-colors font-mono"
                            placeholder="0"
                            min="0"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-zinc-800/30 border border-zinc-800 rounded-xl">
              <div className="pr-4">
                <p className="text-sm font-medium text-zinc-200">Cuenta activa</p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Desactívala para dar de baja la cuenta. Las cuentas dadas de baja no aparecen en el Informe de Tarjetas ni en el saldo total.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                aria-pressed={isActive}
                className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${isActive ? 'bg-emerald-500' : 'bg-zinc-600'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${isActive ? 'translate-x-6' : ''}`} />
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl font-medium transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="flex-1 py-3 px-4 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-xl font-medium transition-all flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {isSaving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
